import {Row, Col} from "react-bootstrap"
import React, {Component, Fragment} from "react"
import {RouteComponentProps} from "react-router-dom"

import linkedCardService from "@irati/perksgo/services/linkedcards"
import authentication from "@irati/auth"
import Hr from "@irati/ui/src/Layout/Hr"
import {H1, Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {SmallCaption} from "@irati/ui/src/Labels"
import Loading from "@irati/ui/src/Loading"
import Card from "@irati/ui/src/Layout/Card"
import History from "@irati/utils/history"
import {FormattedMessage, getResolvedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import {hangPromise} from "@irati/utils/promise"
import notify from "@irati/utils/notify"
import CancellationPopup from "@irati/ui/src/CancellationPopup"
import perksGOTracking from "@irati-tracking/perksgo/perksGO"
import ModalLocation from "@irati-tracking/perksgo/enums/ModalLocation"
import {Card as LinkedCard} from "@irati/types"

import UserLinkedCard from "../../components/UserLinkedCard"
import {PerksGOIcon, RightCol} from "./styled"

interface Props extends InjectIntl, RouteComponentProps<{action: string}> {}

interface State {
  load?: Promise<any>,
  selectedCard?: LinkedCard,
  linkedCards?: LinkedCard[]
}

class PerksGOLinkedCards extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      load: hangPromise()
    }
  }

  private findOutMorePopup: any = React.createRef<any>()
  private cancellation: any = React.createRef<any>()

  componentDidMount() {
    this.setState({load: this.listCards()})
    this.shouldShowModal()
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.shouldShowModal()
    }
  }

  listCards = () => {
    const userId = authentication.user().uuid
    return linkedCardService.getPerksGOCards({userId}).then((linkedCards) => {
      this.setState({linkedCards})
    })
  }

  shouldShowModal = () => {
    if (this.props.match.params.action === "add") {
      this.showModal()
    }
  }

  showModal = () => {
    perksGOTracking.showFindOutMoreModal(ModalLocation.MyLinkedCards)
    this.findOutMorePopup.current.show()
  }

  hideModal = () => {
    History.push("account/settings/linkedcards")
    this.listCards()
  }

  handleShowUnlinkCardPopup = (card: LinkedCard) => {
    perksGOTracking.clickDeleteLinkedCard()

    this.cancellation.current.popup.open()
    this.setState({selectedCard: card})
  }

  handleCloseUnlinkCardPopup = () => {
    perksGOTracking.cancelDeleteLinkedCard()

    this.cancellation.current.popup.close()
    this.setState({selectedCard: undefined})
  }

  handleUnlinkCard = () => {
    const {selectedCard} = this.state

    if (selectedCard) {
      const {uuid: cardUuid} = selectedCard
      linkedCardService
        .unlinkCard({cardUuid})
        .then(() => this.handleUnlinkSuccess(selectedCard))
        .catch((err) => {
          notify.error({
            message: getResolvedMessage(this, "message", err),
            position: "top-right"
          })
          this.handleCloseUnlinkCardPopup()
        })
    }
  }

  handleUnlinkSuccess = (card?: LinkedCard) => {
    if (!card) return
    const {formatMessage} = this.props
    const {linkedCards} = this.state
    const {lastFour} = card

    perksGOTracking.confirmDeleteLinkedCard(linkedCards.length - 1)

    notify.success({
      message: formatMessage({id: "account.linked.unlinkCard.success.message"}, {lastFour}),
      position: "top-right"
    })
    this.listCards()
    this.handleCloseUnlinkCardPopup()
  }

  render() {
    const {formatMessage} = this.props
    const {load, linkedCards} = this.state

    const hasLinkedCards = linkedCards && linkedCards.length > 0

    return (
      <>
        <Card tiny noBorder>
          <Flex justifyContent="space-between" alignItems="center">
            <H1 marginBottom={4}>
              <FormattedMessage id="account.linked.header" />
            </H1>
            {hasLinkedCards && <PerksGOIcon icon="perksGO" size={40} />}
          </Flex>
          <Loading load={load}>
            {hasLinkedCards && (
              <>
                <Body marginBottom={4}><FormattedMessage id="account.linked.perksGOIntro" /></Body>
                <Body marginBottom={3}>
                  <FormattedMessage
                    id="account.linked.perksGONote"
                    values={{
                      strong: (
                        <strong>
                          <FormattedMessage id="account.linked.perksGONoteStrong" />
                        </strong>
                      )
                    }} />
                </Body>
                <Row>
                  <Col xs={6}>
                    <SmallCaption uppercase>
                      <FormattedMessage id="account.linked.card.cardNumber" />
                    </SmallCaption>
                  </Col>
                  <RightCol xs={3}>
                    <SmallCaption uppercase>
                      <FormattedMessage id="account.linked.card.expDate" />
                    </SmallCaption>
                  </RightCol>
                </Row>
                <Hr marginTop={1} />
                {linkedCards.map((card) => (
                  <Fragment key={card.uuid}>
                    <UserLinkedCard card={card} onClick={this.handleShowUnlinkCardPopup} {...card} />
                    <Hr />
                  </Fragment>
                ))}
              </>
            )}
          </Loading>
        </Card>
        <CancellationPopup
          ref={this.cancellation}
          onConfirm={this.handleUnlinkCard}
          onCancel={this.handleCloseUnlinkCardPopup}
          headerText={formatMessage({id: "account.linked.unlinkCardPopup.header"})}
          headerBody={formatMessage({id: "account.linked.unlinkCardPopup.subHeader"})}
          cancelButtonText={formatMessage({id: "account.linked.unlinkCardPopup.button.no"})}
          confirmButtonText={formatMessage({id: "account.linked.unlinkCardPopup.button.yes"})}
          cancelImage={formatMessage({id: "account.linked.cancellationPopUp.image"})} />
      </>
    )
  }
}

export default injectIntl(PerksGOLinkedCards)
